import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LogoMark from "./LogoMark";
import { brand } from "../data/site";

const links = [
    { label: "Legacy", href: "#legacy" },
    { label: "Projects", href: "#projects" },
    { label: "Shop", href: "#hardware" },
    { label: "Services", href: "#services" },
    { label: "Gallery", href: "#gallery" },
    { label: "Contact", href: "#contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <motion.header
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
                className={`fixed top-0 inset-x-0 z-50 transition-[background,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "bg-[#0b0b0b]/80 backdrop-blur-xl border-b border-[rgba(212,175,55,0.25)]"
                        : "bg-transparent border-b border-transparent"
                }`}
                data-testid="site-nav"
            >
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-[72px] flex items-center justify-between">
                    <a
                        href="#top"
                        className="flex items-center gap-3"
                        data-testid="nav-logo"
                    >
                        <LogoMark size={36} animate={false} />
                        <div className="hidden sm:flex flex-col leading-tight">
                            <span className="font-display text-[15px] tracking-[0.14em] uppercase text-white">
                                BRA Sheriff
                            </span>
                            <span className="text-[9px] uppercase tracking-[0.35em] text-[#bebebe]">
                                Builders &amp; Developers
                            </span>
                        </div>
                    </a>

                    <nav className="hidden lg:flex items-center gap-9">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                className="group relative text-[12px] uppercase tracking-[0.28em] text-white/85 hover:text-white transition-colors"
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                            >
                                {l.label}
                                <span className="absolute -bottom-2 left-0 h-px w-0 bg-[#C89B3C] transition-[width] duration-500 group-hover:w-full" />
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4">
                        <a
                            href="#contact"
                            className="btn-sheen hidden md:inline-flex items-center gap-2 px-5 py-3 bg-[#C89B3C] text-[#0b0b0b] text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-[#E2C56D] transition-colors"
                            data-testid="nav-cta"
                        >
                            <span className="relative z-[2]">Book Consultation</span>
                        </a>
                        <button
                            aria-label="Toggle menu"
                            className="lg:hidden w-11 h-11 border border-[rgba(212,175,55,0.35)] text-[#C89B3C] flex items-center justify-center"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="nav-toggle"
                        >
                            <div className="flex flex-col gap-[5px]">
                                <span
                                    className={`block w-4 h-[1.5px] bg-current transition-transform ${
                                        open ? "translate-y-[6.5px] rotate-45" : ""
                                    }`}
                                />
                                <span
                                    className={`block w-4 h-[1.5px] bg-current transition-opacity ${
                                        open ? "opacity-0" : ""
                                    }`}
                                />
                                <span
                                    className={`block w-4 h-[1.5px] bg-current transition-transform ${
                                        open ? "-translate-y-[6.5px] -rotate-45" : ""
                                    }`}
                                />
                            </div>
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-x-0 top-[72px] z-40 bg-[#0b0b0b]/95 backdrop-blur-xl border-b border-[rgba(212,175,55,0.25)] lg:hidden"
                        data-testid="nav-mobile-panel"
                    >
                        <div className="px-6 py-8 flex flex-col gap-6">
                            {links.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-3xl text-white/90 hover:text-[#E2C56D]"
                                    data-testid={`nav-mlink-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href={`tel:${brand.phoneRaw}`}
                                className="mt-4 text-[#C89B3C] text-sm tracking-[0.3em] uppercase"
                                onClick={() => setOpen(false)}
                            >
                                {brand.phone}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
